import React, {
  useEffect,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type SyntheticEvent,
} from 'react';
import isEqual from 'lodash/isEqual';
import {
  Box,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import {
  DescriptionOutlined as DescriptionOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { appFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/styles.ts';
import { DocStatusChip } from '~/modules/covenants/CovenantsDataApplicationForm/components/DocStatusChip.tsx';
import { ClientCommentCell } from '~/components/CommentCell/ClientCommentCell.tsx';
import { TApiApplicationDocument, TApiRequiredDocument } from '~/modules/covenants/types.ts';
import { DocActions } from '~/modules/covenants/CovenantsDataApplicationForm/components/DocAction.tsx';
import { useDocListContext } from '~/modules/covenants/CovenantsDataApplicationForm/components/DocListContext.tsx';
import {
  selectCovenants,
  selectCovenantsError,
  selectCovenantsPageConfiguration,
  selectCovenantsStatus,
} from '~/store/covenants/selectors.ts';
import { AppDispatch } from '~/store/store.ts';
import { fetchDocumentCovenants } from '~/store/covenants/thunks/documentThunks.ts';
import { resetCovenants } from '~/store/covenants/slice.ts';
import {
  COVENANTS_PER_PAGE,
  CovenantsDialog,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/components/CovenantsDialog.tsx';
import { buildApplicationDocumentRowKey } from '~/modules/covenants/utils.ts';
import { DOC_TABLE_ROW_LABELS } from '~/modules/covenants/CovenantsDataApplicationForm/constants.ts';
import {
  getApplicationDocumentDeadlineLabel,
  getApplicationDocumentPeriodLabel,
  shouldShowGroupedDocumentStatus,
  TDocRowDividerVariant,
  TGroupedDocumentRowStatus,
} from '~/modules/covenants/CovenantsDataApplicationForm/utils.ts';

export interface IDocTableRowProps {
  doc: TApiApplicationDocument;
  rowVariant: 'document' | 'file';
  groupHasFiles: boolean;
  isExpanded: boolean;
  dividerVariant?: TDocRowDividerVariant;
  onToggleExpand?: () => void;
  groupedStatus?: TGroupedDocumentRowStatus | null;
}

const areDocTableRowPropsEqual = (
  prev: Readonly<IDocTableRowProps>,
  next: Readonly<IDocTableRowProps>
) =>
  isEqual(
    {
      doc: prev.doc,
      rowVariant: prev.rowVariant,
      groupHasFiles: prev.groupHasFiles,
      isExpanded: prev.isExpanded,
      dividerVariant: prev.dividerVariant,
      groupedStatus: prev.groupedStatus,
    },
    {
      doc: next.doc,
      rowVariant: next.rowVariant,
      groupHasFiles: next.groupHasFiles,
      isExpanded: next.isExpanded,
      dividerVariant: next.dividerVariant,
      groupedStatus: next.groupedStatus,
    }
  );

export const DocTableRow: React.FC<IDocTableRowProps> = React.memo(
  ({
    doc,
    rowVariant,
    groupHasFiles,
    isExpanded,
    dividerVariant = 'full',
    onToggleExpand,
    groupedStatus = null,
  }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { applicationStatus, contragentId, showDeadlineColumn, localFiles, isReadOnly } =
      useDocListContext();
    const [selectedDoc, setSelectedDoc] = useState<TApiRequiredDocument | null>(null);
    const [covenantsOpen, setCovenantsOpen] = useState(false);

    const covenants = useSelector(selectCovenants);
    const covenantsPageConfiguration = useSelector(selectCovenantsPageConfiguration);
    const covenantsStatus = useSelector(selectCovenantsStatus);
    const covenantsError = useSelector(selectCovenantsError);
    const covenantsLoading = covenantsStatus === 'loading';
    const isDocumentRow = rowVariant === 'document';
    const rowKey = buildApplicationDocumentRowKey(doc);
    const localFile = localFiles[rowKey];
    const hasFile = !!doc.fileName || !!localFile?.file;
    const secondaryText = localFile?.file?.name ?? doc.fileName ?? DOC_TABLE_ROW_LABELS.missingFile;
    const periodLabel = getApplicationDocumentPeriodLabel(doc);
    const showGroupedStatus =
      isDocumentRow &&
      !!groupedStatus &&
      shouldShowGroupedDocumentStatus(applicationStatus, isExpanded);
    const isExpandableRow = isDocumentRow && groupHasFiles && !!onToggleExpand;

    const handleOpenCovenants = () => {
      setSelectedDoc({
        id: doc.documentId,
        displayName: doc.documentName,
        childPeriod: doc.childPeriod,
        yearPeriod: doc.yearPeriod,
        deadline: doc.deadline || '',
        status: 'PENDING',
      });
      setCovenantsOpen(true);

      dispatch(
        fetchDocumentCovenants({
          contragentId,
          documentId: doc.documentId,
          yearPeriod: doc.yearPeriod,
          childPeriod: doc.childPeriod,
          deadline: doc.deadline || null,
          page: 0,
          size: COVENANTS_PER_PAGE,
        })
      );
    };

    const handleCloseCovenants = () => {
      setCovenantsOpen(false);
      setSelectedDoc(null);
      dispatch(resetCovenants());
    };

    const handleCovenantsPageChange = (nextPage: number) => {
      if (!selectedDoc) {
        return;
      }

      dispatch(
        fetchDocumentCovenants({
          contragentId,
          documentId: selectedDoc.id,
          yearPeriod: selectedDoc.yearPeriod,
          childPeriod: selectedDoc.childPeriod,
          deadline: selectedDoc.deadline || null,
          page: nextPage,
          size: COVENANTS_PER_PAGE,
        })
      );
    };

    useEffect(() => {
      return () => {
        dispatch(resetCovenants());
      };
    }, [dispatch]);

    const handleToggleExpand = (event?: MouseEvent | KeyboardEvent) => {
      event?.stopPropagation();
      onToggleExpand?.();
    };

    const handleRowClick = () => {
      if (!isExpandableRow) {
        return;
      }

      onToggleExpand?.();
    };

    const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
      if (!isExpandableRow) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggleExpand?.();
      }
    };

    const stopRowToggle = (event: SyntheticEvent) => {
      event.stopPropagation();
    };

    return (
      <>
        <TableRow
          sx={appFormStyles.row({
            isDocumentRow,
            isReadOnly,
            isExpandable: isExpandableRow,
            dividerVariant,
          })}
          onClick={handleRowClick}
          onKeyDown={handleRowKeyDown}
          tabIndex={isExpandableRow ? 0 : -1}
          aria-expanded={isExpandableRow ? isExpanded : undefined}
        >
          <TableCell sx={appFormStyles.nameCell(isDocumentRow)}>
            <Box sx={appFormStyles.nameContent}>
              {isDocumentRow ? (
                <>
                <Box sx={appFormStyles.documentToggleWrap}>
                  {groupHasFiles ? (
                    <IconButton
                      size="small"
                      onClick={handleToggleExpand}
                      sx={appFormStyles.expandButton}
                      aria-label={
                        isExpanded
                          ? DOC_TABLE_ROW_LABELS.collapseDocument
                          : DOC_TABLE_ROW_LABELS.expandDocument
                      }
                    >
                      {isExpanded ? (
                        <KeyboardArrowUpIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="small" />
                      )}
                    </IconButton>
                  ) : (
                    <Box sx={appFormStyles.expandButtonPlaceholder} />
                  )}
                </Box>

                <Typography component="span" className="document-row-name" sx={appFormStyles.documentName}>
                  {doc.documentName}
                </Typography>
                </>
              ) : (
                <Box sx={appFormStyles.fileNameWrap}>
                  <DescriptionOutlinedIcon sx={appFormStyles.fileIcon} />
                  <Typography component="span" sx={appFormStyles.fileName}>
                    {secondaryText}
                  </Typography>
                </Box>
              )}
            </Box>
          </TableCell>

          <TableCell align="left" sx={appFormStyles.commentCell}>
            {!isDocumentRow && (
              <ClientCommentCell initialComment={doc.comment} />
            )}
          </TableCell>

          <TableCell align="left" sx={appFormStyles.dataCell}>
            {isDocumentRow && (
              <Typography variant="body2" sx={appFormStyles.periodText}>
                {periodLabel}
              </Typography>
            )}
          </TableCell>

          {showDeadlineColumn && (
            <TableCell align="left" sx={appFormStyles.dataCell}>
              {isDocumentRow && (
                <Typography variant="body2" sx={appFormStyles.deadlineText}>
                  {getApplicationDocumentDeadlineLabel(doc.deadline)}
                </Typography>
              )}
            </TableCell>
          )}

          <TableCell align="left" sx={appFormStyles.statusCell}>
            {showGroupedStatus && groupedStatus && <DocStatusChip doc={doc} status={groupedStatus} />}
            {!isDocumentRow && hasFile && <DocStatusChip doc={doc} />}
          </TableCell>

          <TableCell
            align="left"
            sx={appFormStyles.actionsCell}
            onClick={stopRowToggle}
            onMouseDown={stopRowToggle}
            onKeyDown={stopRowToggle}
          >
            <Box
              sx={appFormStyles.actionsWrap(isDocumentRow)}
              onClick={stopRowToggle}
              onMouseDown={stopRowToggle}
            >
              <DocActions
                mode={rowVariant}
                doc={doc}
                groupHasFiles={groupHasFiles}
                onOpenCovenants={handleOpenCovenants}
              />
            </Box>
          </TableCell>
        </TableRow>

        <CovenantsDialog
          open={covenantsOpen}
          onClose={handleCloseCovenants}
          document={selectedDoc}
          covenants={covenants}
          covenantsTotalElements={covenantsPageConfiguration.totalElements}
          isLoading={covenantsLoading}
          error={covenantsError}
          onPageChange={handleCovenantsPageChange}
        />
      </>
    );
  },
  areDocTableRowPropsEqual
);
